import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";

import LoadingIcon from "../ui/LoadingIcon";
import SubmitIcon from "../ui/SubmitIcon";
import { Common } from "@src/styles/Common";

interface OwnProps {
    onSubmit?: (value: string) => void;
    onCancel: () => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    onFocus?: () => void;
}

const ChatInput = ({ onSubmit, onCancel, isLoading, isDisabled = false, onFocus }: OwnProps) => {
    const [value, setValue] = useState("");
    const [isComposing, setIsComposing] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const handleResizeHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "auto";

            const lineHeight = 20;
            const maxRows = 4;
            const maxHeight = maxRows * lineHeight;

            const scrollHeight = textarea.scrollHeight;

            textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
        handleResizeHeight();
    };

    const handleSubmit = () => {
        if (isLoading || !value.trim()) return;

        onSubmit?.(value);

        if (!isLoading) {
            setValue("");
        }

        setTimeout(handleResizeHeight, 0);
    };

    return (
        <ChatInputContainer>
            <Input
                maxLength={200}
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                rows={1}
                placeholder="무엇이든 물어보세요. (최대 200자)"
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !isLoading && !isComposing) {
                        e.preventDefault();
                        handleSubmit();
                    }
                }}
                disabled={isDisabled}
                onFocus={onFocus}
            />

            <SubmitButton
                type="button"
                onClick={() => {
                    if (!isLoading) {
                        handleSubmit();
                    } else {
                        onCancel();
                    }
                }}
                style={{ backgroundColor: value !== "" || isLoading ? "#000" : Common.colors.grey04 }}
            >
                {isLoading ? <LoadingIcon /> : <SubmitIcon />}
            </SubmitButton>
        </ChatInputContainer>
    );
};

const ChatInputContainer = styled.div`
    width: 100%;
    box-sizing: border-box;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 13px 12px;
    border-radius: 5px;

    background-color: ${Common.colors.grey02};
`;

const Input = styled.textarea`
    width: 100%;
    height: 20px;
    padding: 0;
    margin-right: 12px;
    border: none;
    outline: none;
    resize: none;
    overflow-y: auto;
    font-size: 14px;
    line-height: 140%;
    font-family: inherit;
    background-color: ${Common.colors.grey02};

    &::placeholder {
        color: #9e9e9e;
    }
`;

const SubmitButton = styled.button`
    min-width: 28px;
    min-height: 28px;
    padding: 4px;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: #0056b3;
    }

    svg {
        fill: white;
    }
`;

export default ChatInput;
